import type { FC, ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from 'src/utils/axios';
import { fDate, fDateTime } from 'src/utils/formatTime';
import {
  fileKindLabel,
  formatFileSize,
} from 'src/constants/supportCaseAttachments';
import type {
  SupportDocument,
  SupportSchedule,
} from 'src/components/SupportCase/types';
import { PANEL_TOOLTIP_SLOT_PROPS } from 'src/components/SupportCase/panelTooltip';
import FilePreviewDialog from 'src/components/SupportCase/FilePreviewDialog';

/**
 * The two collapsible sections a case can carry (ENG-4185):
 * **the files in it**, and **the standing instructions that open cases like it**.
 *
 * Both are collapsed by default. A case is a conversation, and a conversation with a
 * permanent file list above it is harder to read; the sections are how you check
 * "what did I send" without scrolling, and how you find the schedule that produced an
 * answer at 08:00 on a Monday.
 */

const SectionHeading: FC<{ title: string; count?: number }> = ({
  title,
  count,
}) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <Typography variant="subtitle2">{title}</Typography>
    {typeof count === 'number' ? (
      <Typography variant="caption" color="text.secondary">
        {count}
      </Typography>
    ) : null}
  </Stack>
);

const Row: FC<{ children: ReactNode }> = ({ children }) => (
  <Stack
    direction="row"
    spacing={1}
    alignItems="center"
    justifyContent="space-between"
    sx={{ py: 0.75 }}
  >
    {children}
  </Stack>
);

export const CaseFiles: FC<{
  documents: SupportDocument[];
  onRemoved?: () => void;
}> = ({ documents, onRemoved }) => {
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<number | null>(null);
  // PA-11 — the file opens here, in the product, instead of leaving for a download.
  const [previewing, setPreviewing] = useState<SupportDocument | null>(null);

  useEffect(() => setError(null), [documents]);

  const remove = useCallback(
    async (document: SupportDocument) => {
      setRemoving(document.id);
      setError(null);
      try {
        await axios.delete(`/support/documents/${document.id}/`);
        onRemoved?.();
      } catch {
        setError(`Could not remove ${document.name}. Try again.`);
      } finally {
        setRemoving(null);
      }
    },
    [onRemoved]
  );

  if (documents.length === 0) {
    return null;
  }

  return (
    <Accordion disableGutters elevation={0} data-testid="case-files">
      <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
        <SectionHeading
          title="Documents in this conversation"
          count={documents.length}
        />
      </AccordionSummary>
      <Box sx={{ px: 2, pb: 1.5 }}>
        {error ? (
          <Alert severity="error" sx={{ py: 0, mb: 1 }}>
            {error}
          </Alert>
        ) : null}
        <Stack divider={<Divider flexItem />}>
          {documents.slice(0, 10).map((document) => (
            <Row key={document.id}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" noWrap title={document.name}>
                  {document.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {fileKindLabel(document.name)} ·{' '}
                  {formatFileSize(document.size)} ·{' '}
                  {document.origin === 'agent' ? 'Pronto Assistant' : 'You'} ·{' '}
                  {document.created_at ? fDateTime(document.created_at) : '—'}
                </Typography>
              </Box>
              <Stack direction="row" spacing={0.25}>
                <Tooltip slotProps={PANEL_TOOLTIP_SLOT_PROPS} title="View">
                  <IconButton
                    size="small"
                    onClick={() => setPreviewing(document)}
                    aria-label={`View ${document.name}`}
                  >
                    <VisibilityOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip slotProps={PANEL_TOOLTIP_SLOT_PROPS} title="Download">
                  <IconButton
                    size="small"
                    component="a"
                    href={`/api/support/documents/${document.id}/download/`}
                    aria-label={`Download ${document.name}`}
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip slotProps={PANEL_TOOLTIP_SLOT_PROPS} title="Remove">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => void remove(document)}
                      disabled={removing === document.id}
                      aria-label={`Remove ${document.name}`}
                    >
                      {removing === document.id ? (
                        <CircularProgress size={14} />
                      ) : (
                        <DeleteOutlineIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Row>
          ))}
        </Stack>
      </Box>
      <FilePreviewDialog
        open={previewing !== null}
        onClose={() => setPreviewing(null)}
        document={previewing}
      />
    </Accordion>
  );
};

export const CaseSchedules: FC<{
  enabled: boolean;
  /** The case the section sits in, so its own schedule can be shown as "this one". */
  caseId: number | null;
  /**
   * Open on mount (ENG-4211).
   *
   * Inside the case panel this is a collapsed section under the conversation, which is
   * right — it is a footnote there. As the body of the hub's Schedules tab it is the
   * whole page, and a page that renders one closed accordion reads as broken. Default
   * false keeps the panel exactly as it was.
   */
  defaultExpanded?: boolean;
}> = ({ enabled, caseId, defaultExpanded = false }) => {
  const [schedules, setSchedules] = useState<SupportSchedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get<{ schedules: SupportSchedule[] }>(
        '/support/schedules/'
      );
      setSchedules(response.data.schedules ?? []);
    } catch {
      setError('Could not load your scheduled actions.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (enabled) {
      void load();
    }
  }, [enabled, load]);

  const toggle = useCallback(async (schedule: SupportSchedule) => {
    try {
      const response = await axios.patch<SupportSchedule>(
        `/support/schedules/${schedule.id}/`,
        { enabled: !schedule.enabled }
      );
      setSchedules((current) =>
        current.map((row) => (row.id === schedule.id ? response.data : row))
      );
    } catch {
      setError('That change did not save. Try again.');
    }
  }, []);

  const remove = useCallback(async (schedule: SupportSchedule) => {
    try {
      await axios.delete(`/support/schedules/${schedule.id}/`);
      setSchedules((current) =>
        current.filter((row) => row.id !== schedule.id)
      );
    } catch {
      setError('That scheduled action could not be removed. Try again.');
    }
  }, []);

  if (!enabled) {
    return null;
  }

  return (
    <Accordion
      disableGutters
      elevation={0}
      data-testid="case-schedules"
      defaultExpanded={defaultExpanded}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
        <SectionHeading title="Scheduled actions" count={schedules.length} />
      </AccordionSummary>
      <Box sx={{ px: 2, pb: 1.5 }}>
        {error ? (
          <Alert severity="error" sx={{ py: 0, mb: 1 }}>
            {error}
          </Alert>
        ) : null}
        {loading && schedules.length === 0 ? (
          <Stack alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={20} />
          </Stack>
        ) : null}
        {!loading && schedules.length === 0 ? (
          <Box sx={{ py: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Nothing is scheduled. Ask the Pronto Assistant to check something
              on a schedule and it will appear here.
            </Typography>
          </Box>
        ) : null}
        <Stack divider={<Divider flexItem />}>
          {schedules.map((schedule) => (
            <Row key={schedule.id}>
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Typography variant="body2" noWrap>
                    {schedule.name}
                  </Typography>
                  {/* `caseId` is null on the hub's Schedules tab, where the list is
                      every schedule rather than this case's — and a schedule that has
                      never run also has a null `last_case`, so a bare equality check
                      labelled every untouched schedule "This case" (ENG-4211). */}
                  {caseId !== null && schedule.last_case === caseId ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      label="This conversation"
                    />
                  ) : null}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {schedule.enabled ? 'Next' : 'Paused'} ·{' '}
                  {schedule.enabled && schedule.next_run_at
                    ? `${fDate(schedule.next_run_at)} ${schedule.time_label}`
                    : 'no next run'}{' '}
                  · {schedule.days.join(', ') || 'every day'}
                </Typography>
              </Box>
              <Stack direction="row" spacing={0.25}>
                <Button
                  size="small"
                  onClick={() => void toggle(schedule)}
                  data-testid={`toggle-schedule-${schedule.id}`}
                >
                  {schedule.enabled ? 'Pause' : 'Resume'}
                </Button>
                <Tooltip slotProps={PANEL_TOOLTIP_SLOT_PROPS} title="Delete">
                  <IconButton
                    size="small"
                    onClick={() => void remove(schedule)}
                    aria-label={`Delete ${schedule.name}`}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Row>
          ))}
        </Stack>
      </Box>
    </Accordion>
  );
};

export default CaseFiles;
